import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  structuredData?: object | object[];
  ogImage?: string;
  ogType?: "website" | "article" | "product" | "profile";
  canonicalUrl?: string;
  noindex?: boolean;
  /** Optional list of <link rel="alternate" hreflang> entries to emit */
  alternates?: Array<{ hreflang: string; href: string }>;
}

const SITE_ORIGIN = "https://realtyblocks.co.in";
const DEFAULT_OG = `${SITE_ORIGIN}/assets/images/og-realtyblocks.png`;

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function absolutizeUrl(input?: string): string {
  if (!input) return typeof window !== "undefined" ? window.location.href : SITE_ORIGIN;
  if (/^https?:\/\//i.test(input)) return input;
  if (typeof window !== "undefined") {
    try {
      return new URL(input, window.location.origin).toString();
    } catch {
      return input;
    }
  }
  return input.startsWith("/") ? `${SITE_ORIGIN}${input}` : `${SITE_ORIGIN}/${input}`;
}

export default function SEOHead({
  title,
  description,
  keywords,
  structuredData,
  ogImage = DEFAULT_OG,
  ogType = "website",
  canonicalUrl,
  noindex = false,
  alternates,
}: SEOHeadProps) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("hi") ? "hi" : "en";
  const ogLocale = lang === "hi" ? "hi_IN" : "en_IN";

  useEffect(() => {
    const url = absolutizeUrl(canonicalUrl);
    const image = absolutizeUrl(ogImage);

    document.title = title;
    document.documentElement.lang = lang;

    upsertMeta("name", "description", description);
    if (keywords) upsertMeta("name", "keywords", keywords);

    // Robots / AI-bot directives. Tuned per-page so admin/preview routes
    // can opt out of indexing while public pages stay crawlable with
    // generous snippet/image previews for rich SERP and AI-overview cards.
    const robotsContent = noindex
      ? "noindex, nofollow, noarchive, nosnippet"
      : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";
    upsertMeta("name", "robots", robotsContent);
    upsertMeta("name", "googlebot", robotsContent);
    upsertMeta("name", "bingbot", robotsContent);

    // Search-engine ownership verification tokens. The Express HTML
    // rewriter injects these for non-JS crawlers via the
    // `<!-- @verification-meta -->` marker, but we also upsert here as a
    // belt-and-braces fallback for SPA navigations and static/CDN deploys
    // that may bypass the server rewriter. Tokens are read from build-time
    // Vite env vars so they're configurable per environment.
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env || {};
    const gsc = (env.VITE_GSC_VERIFICATION || env.VITE_GOOGLE_SITE_VERIFICATION || "").trim();
    const bing = (
      env.VITE_BING_VERIFICATION ||
      env.VITE_BING_WEBMASTER_VERIFICATION ||
      env.VITE_BING_SITE_VERIFICATION ||
      ""
    ).trim();
    if (gsc) upsertMeta("name", "google-site-verification", gsc);
    if (bing) upsertMeta("name", "msvalidate.01", bing);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:image:alt", title);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:site_name", "RealtyBlocks");
    upsertMeta("property", "og:locale", ogLocale);
    upsertMeta("property", "og:locale:alternate", ogLocale === "en_IN" ? "hi_IN" : "en_IN");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);
    upsertMeta("name", "twitter:image:alt", title);
    upsertMeta("name", "twitter:url", url);
    upsertMeta("name", "twitter:site", "@RealtyBlocks");
    upsertMeta("name", "twitter:creator", "@RealtyBlocks");

    let canonical = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = url;

    // hreflang alternates: emit defaults (en-IN, hi-IN, x-default) plus any
    // page-specific overrides. We strip *all* existing alternate links —
    // including the static ones in index.html that all point at "/" — to
    // avoid hreflang conflicts on non-home routes.
    const existing = document.querySelectorAll('link[rel="alternate"][hreflang]');
    existing.forEach((el) => el.remove());

    const altList: Array<{ hreflang: string; href: string }> = alternates && alternates.length
      ? alternates
      : [
          { hreflang: "en-IN", href: url },
          { hreflang: "hi-IN", href: url },
          { hreflang: "x-default", href: url },
        ];

    const addedLinks: HTMLLinkElement[] = [];
    for (const alt of altList) {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = alt.hreflang;
      link.href = alt.href;
      link.setAttribute("data-seo-hreflang", "true");
      document.head.appendChild(link);
      addedLinks.push(link);
    }

    // JSON-LD: only manage scripts we add (data-seo-dynamic). When a
    // dynamic block carries an "@id" that already exists in a static
    // index.html block, hide that static block while we're mounted so
    // the dynamic one (which may include aggregateRating/review) is
    // canonical instead of competing with the stale static copy.
    const added: HTMLScriptElement[] = [];
    const hiddenStatic: HTMLScriptElement[] = [];
    if (structuredData) {
      const blocks = Array.isArray(structuredData)
        ? structuredData
        : [structuredData];
      const dynamicIds = new Set<string>();
      for (const block of blocks) {
        const id = (block as { "@id"?: unknown })?.["@id"];
        if (typeof id === "string") dynamicIds.add(id);
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-seo-dynamic", "true");
        s.text = JSON.stringify(block);
        document.head.appendChild(s);
        added.push(s);
      }
      if (dynamicIds.size > 0) {
        const allLd = document.head.querySelectorAll<HTMLScriptElement>(
          'script[type="application/ld+json"]:not([data-seo-dynamic])',
        );
        allLd.forEach((s) => {
          try {
            const parsed = JSON.parse(s.textContent || "null");
            const id = parsed && typeof parsed === "object" ? parsed["@id"] : null;
            if (typeof id === "string" && dynamicIds.has(id)) {
              s.setAttribute("data-seo-static-hidden", "true");
              s.type = "application/ld+json-disabled";
              hiddenStatic.push(s);
            }
          } catch {
            // ignore non-JSON blocks
          }
        });
      }
    }

    return () => {
      for (const s of added) s.remove();
      for (const s of hiddenStatic) {
        s.type = "application/ld+json";
        s.removeAttribute("data-seo-static-hidden");
      }
      for (const l of addedLinks) l.remove();
    };
  }, [title, description, keywords, structuredData, ogImage, ogType, canonicalUrl, lang, ogLocale, noindex, alternates]);

  return null;
}
